import React, { useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D', '#A4DE6C'];

function Leaderboard({ data }) {
  // Get previous week's date range (Sunday to Saturday)
  const getLastWeekRange = () => {
    const today = dayjs();
    const currentDayOfWeek = today.day(); // 0 is Sunday, 1 is Monday, etc.
    
    // Calculate last Saturday
    const lastSaturday = today.subtract(currentDayOfWeek + 1, 'day');
    // Calculate last Sunday (6 days before last Saturday)
    const lastSunday = lastSaturday.subtract(6, 'day');
    
    return {
      start: lastSunday.startOf('day'),
      end: lastSaturday.endOf('day')
    };
  };

  const defaultDateRange = getLastWeekRange();
  const [dateRange, setDateRange] = useState({
    start: defaultDateRange.start,
    end: defaultDateRange.end
  });
  const [orderBy, setOrderBy] = useState('totalMeetings');
  const [order, setOrder] = useState('desc');

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const formatManagerName = (email) => {
    if (!email) return 'N/A';
    return email.split('@')[0]
      .split(/[._]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  // Get filtered data based on date range - without deduplication
  const filteredData = useMemo(() => {
    // Don't deduplicate meetings for the leaderboard
    return data.filter(meeting => {
      const meetingDate = dayjs(meeting['Meeting Date']);
      return meetingDate.isAfter(dateRange.start) && 
             meetingDate.isBefore(dateRange.end);
    });
  }, [data, dateRange]);

  // Calculate leaderboard metrics with filtered data
  const leaderboardMetrics = useMemo(() => {
    console.log('Raw data in Leaderboard:', filteredData);
    const metrics = new Map();
    const processedMeetings = new Map(); // Track meetings by ID and their exporters
    
    // First pass: Group meetings by ID and collect all exporters
    filteredData.forEach(meeting => {
      const meetingId = meeting['Meeting ID'];
      const exportedBy = meeting['Exported By'];
      if (!meetingId || !exportedBy) return;

      if (!processedMeetings.has(meetingId)) {
        processedMeetings.set(meetingId, {
          meeting,
          exporters: new Set([exportedBy])
        });
      } else {
        // Add additional exporters to the same meeting
        processedMeetings.get(meetingId).exporters.add(exportedBy);
      }
    });

    // Second pass: Process meetings and credit all exporters
    processedMeetings.forEach(({ meeting, exporters }) => {
      exporters.forEach(exportedBy => {
        if (!metrics.has(exportedBy)) {
          metrics.set(exportedBy, {
            name: exportedBy,
            totalMeetings: 0,
            uniquePartners: new Set(),
            meetingTypes: new Map(),
            meetings: []
          });
        }
        
        const userMetrics = metrics.get(exportedBy);
        userMetrics.totalMeetings++;
        userMetrics.uniquePartners.add(meeting['Partner Name']);
        userMetrics.meetings.push(meeting);
        
        const meetingType = meeting['Meeting Type'] || 'Other';
        userMetrics.meetingTypes.set(
          meetingType,
          (userMetrics.meetingTypes.get(meetingType) || 0) + 1
        );
      });
    });

    const result = Array.from(metrics.values())
      .map(metric => ({
        ...metric,
        uniquePartners: metric.uniquePartners.size,
        meetingTypes: Object.fromEntries(metric.meetingTypes),
        meetings: metric.meetings
      }))
      .sort((a, b) => b.totalMeetings - a.totalMeetings);
    
    console.log('Leaderboard metrics:', result);
    return result;
  }, [filteredData]);

  // Calculate weekly metrics without deduplication
  const getWeeklyStats = useMemo(() => {
    // Get the start of the first week and end of the last week
    const startDate = dateRange.start.startOf('week');
    const endDate = dateRange.end.endOf('week');

    // Create an array of week ranges
    const weekRanges = [];
    let currentWeek = startDate;
    while (currentWeek.isBefore(endDate) || currentWeek.isSame(endDate, 'week')) {
      weekRanges.push({
        weekStart: currentWeek,
        weekEnd: currentWeek.endOf('week'),
        weekKey: currentWeek.format('YYYY-MM-DD')
      });
      currentWeek = currentWeek.add(1, 'week');
    }

    // Initialize stats object with all weeks
    const weeklyStats = weekRanges.reduce((acc, { weekKey, weekStart, weekEnd }) => {
      acc[weekKey] = {
        weekStart,
        weekEnd,
        totalMeetings: 0,
        pioInterlocks: 0,
        paoInterlocks: 0,
        managerMeetings: {},
        processedMeetings: new Map()
      };
      return acc;
    }, {});

    // First pass: Group meetings by ID and collect all exporters for each week
    filteredData.forEach(meeting => {
      const meetingDate = dayjs(meeting['Meeting Date']);
      const weekStart = meetingDate.startOf('week');
      const weekKey = weekStart.format('YYYY-MM-DD');
      const meetingId = meeting['Meeting ID'];
      const exportedBy = meeting['Exported By'];

      // Skip if meeting is not in one of our week ranges or missing required data
      if (!weeklyStats[weekKey] || !meetingId || !exportedBy) return;

      if (!weeklyStats[weekKey].processedMeetings.has(meetingId)) {
        weeklyStats[weekKey].processedMeetings.set(meetingId, {
          meeting,
          exporters: new Set([exportedBy])
        });
      } else {
        // Add additional exporters to the same meeting
        weeklyStats[weekKey].processedMeetings.get(meetingId).exporters.add(exportedBy);
      }
    });

    // Second pass: Process meetings and credit all exporters
    Object.keys(weeklyStats).forEach(weekKey => {
      weeklyStats[weekKey].processedMeetings.forEach(({ meeting, exporters }) => {
        exporters.forEach(exportedBy => {
          if (!weeklyStats[weekKey].managerMeetings[exportedBy]) {
            weeklyStats[weekKey].managerMeetings[exportedBy] = {
              total: 0,
              pio: 0,
              pao: 0
            };
          }

          // Increment total meetings for this manager in this week
          weeklyStats[weekKey].managerMeetings[exportedBy].total += 1;
          weeklyStats[weekKey].totalMeetings += 1;

          // Track interlocks
          const meetingType = meeting['Meeting Type'] || 'Other';
          if (meetingType === 'PIO Interlock' || meetingType === 'PIO') {
            weeklyStats[weekKey].pioInterlocks += 1;
            weeklyStats[weekKey].managerMeetings[exportedBy].pio += 1;
          }
          if (meetingType === 'PAO Interlock' || meetingType === 'PAO') {
            weeklyStats[weekKey].paoInterlocks += 1;
            weeklyStats[weekKey].managerMeetings[exportedBy].pao += 1;
          }
        });
      });
    });

    // Convert to array and sort by week
    return Object.entries(weeklyStats)
      .map(([key, stats]) => {
        // Remove the processedMeetings map as it's not needed in the final output
        const { processedMeetings, ...rest } = stats;
        return {
          weekStart: rest.weekStart,
          weekEnd: rest.weekEnd,
          weekRange: `${rest.weekStart.format('MMM D')} - ${rest.weekEnd.format('MMM D')}`,
          totalMeetings: rest.totalMeetings,
          pioInterlocks: rest.pioInterlocks,
          paoInterlocks: rest.paoInterlocks,
          managerMeetings: rest.managerMeetings
        };
      })
      .sort((a, b) => a.weekStart.diff(b.weekStart));
  }, [filteredData, dateRange]);

  // Get all unique managers
  const managers = useMemo(() => {
    const managerSet = new Set();
    getWeeklyStats.forEach(week => {
      Object.keys(week.managerMeetings).forEach(manager => {
        managerSet.add(manager);
      });
    });
    return Array.from(managerSet);
  }, [getWeeklyStats]);

  // Calculate manager totals
  const managerTotals = useMemo(() => {
    return managers.reduce((acc, manager) => {
      acc[manager] = {
        total: getWeeklyStats.reduce((sum, week) => {
          return sum + (week.managerMeetings[manager]?.total || 0);
        }, 0),
        pio: getWeeklyStats.reduce((sum, week) => {
          return sum + (week.managerMeetings[manager]?.pio || 0);
        }, 0),
        pao: getWeeklyStats.reduce((sum, week) => {
          return sum + (week.managerMeetings[manager]?.pao || 0);
        }, 0)
      };
      return acc;
    }, {});
  }, [getWeeklyStats, managers]);

  // Get managers sorted by their respective totals for each section
  const getSortedManagersForTable = (type) => {
    return [...managers].sort((a, b) => {
      const totalA = type === 'total' 
        ? managerTotals[a].total 
        : type === 'pio'
          ? managerTotals[a].pio
          : managerTotals[a].pao;
      
      const totalB = type === 'total'
        ? managerTotals[b].total
        : type === 'pio'
          ? managerTotals[b].pio
          : managerTotals[b].pao;
          
      return totalB - totalA; // Sort descending
    });
  };

  const WeeklyStatsTable = ({ title, type }) => {
    // Get sorted managers for this specific table
    const sortedManagersList = getSortedManagersForTable(type);

    return (
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '200px' }}>
                  Partner Manager
                </TableCell>
                {getWeeklyStats.map(week => (
                  <TableCell 
                    key={week.weekStart.format('YYYY-MM-DD')} 
                    align="right"
                    sx={{ fontWeight: 'bold', minWidth: '100px' }}
                  >
                    {week.weekStart.format('MMM D')} - {week.weekEnd.format('MMM D')}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
                  Total
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', minWidth: '100px' }}>
                  Avg/Week
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedManagersList.map(manager => {
                const weekCount = getWeeklyStats.length;
                const total = type === 'total' 
                  ? managerTotals[manager].total 
                  : type === 'pio' 
                    ? managerTotals[manager].pio
                    : managerTotals[manager].pao;
                
                return (
                  <TableRow key={manager}>
                    <TableCell>{formatManagerName(manager)}</TableCell>
                    {getWeeklyStats.map(week => (
                      <TableCell 
                        key={week.weekStart.format('YYYY-MM-DD')} 
                        align="right"
                      >
                        {type === 'total' 
                          ? week.managerMeetings[manager]?.total || 0
                          : type === 'pio'
                            ? week.managerMeetings[manager]?.pio || 0
                            : week.managerMeetings[manager]?.pao || 0}
                      </TableCell>
                    ))}
                    <TableCell align="right">{total}</TableCell>
                    <TableCell align="right">
                      {weekCount > 0 ? (total / weekCount).toFixed(1) : '0.0'}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Weekly Total</TableCell>
                {getWeeklyStats.map(week => (
                  <TableCell 
                    key={week.weekStart.format('YYYY-MM-DD')} 
                    align="right"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {type === 'total' 
                      ? week.totalMeetings
                      : type === 'pio'
                        ? week.pioInterlocks
                        : week.paoInterlocks}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {getWeeklyStats.reduce((sum, week) => {
                    return sum + (type === 'total' 
                      ? week.totalMeetings
                      : type === 'pio'
                        ? week.pioInterlocks
                        : week.paoInterlocks);
                  }, 0)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                  {(getWeeklyStats.reduce((sum, week) => {
                    return sum + (type === 'total' 
                      ? week.totalMeetings
                      : type === 'pio'
                        ? week.pioInterlocks
                        : week.paoInterlocks);
                  }, 0) / (getWeeklyStats.length || 1)).toFixed(1)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  // Get top manager stats for the summary boxes
  const topManagerStats = useMemo(() => {
    if (leaderboardMetrics.length === 0) return null;
    
    // Sort by total meetings
    const topManager = [...leaderboardMetrics]
      .sort((a, b) => b.totalMeetings - a.totalMeetings)[0];
    
    // Sort by unique partners
    const mostPartnersManager = [...leaderboardMetrics]
      .sort((a, b) => b.uniquePartners - a.uniquePartners)[0];
    
    // Sort by total interlocks (PIO + PAO)
    const mostInterlocksManager = [...leaderboardMetrics]
      .sort((a, b) => {
        const aInterlocks = (a.meetingTypes['PIO Interlock'] || 0) + (a.meetingTypes['PAO Interlock'] || 0) + 
                         (a.meetingTypes['PIO'] || 0) + (a.meetingTypes['PAO'] || 0);
        const bInterlocks = (b.meetingTypes['PIO Interlock'] || 0) + (b.meetingTypes['PAO Interlock'] || 0) +
                         (b.meetingTypes['PIO'] || 0) + (b.meetingTypes['PAO'] || 0);
        return bInterlocks - aInterlocks;
      })[0];

    return {
      topManager: {
        name: formatManagerName(topManager.name),
        meetings: topManager.totalMeetings
      },
      mostPartners: {
        name: formatManagerName(mostPartnersManager.name),
        partners: mostPartnersManager.uniquePartners
      },
      mostInterlocks: {
        name: formatManagerName(mostInterlocksManager.name),
        interlocks: (mostInterlocksManager.meetingTypes['PIO Interlock'] || 0) + 
                   (mostInterlocksManager.meetingTypes['PAO Interlock'] || 0) +
                   (mostInterlocksManager.meetingTypes['PIO'] || 0) + 
                   (mostInterlocksManager.meetingTypes['PAO'] || 0)
      }
    };
  }, [leaderboardMetrics]);

  // Get top 5 managers for the chart, sorted by total meetings
  const topManagers = useMemo(() => {
    const managerStats = leaderboardMetrics
      .sort((a, b) => b.totalMeetings - a.totalMeetings)
      .slice(0, 5)
      .map(manager => ({
        name: formatManagerName(manager.name),
        totalMeetings: manager.totalMeetings,
        pioMeetings: (manager.meetingTypes['PIO Interlock'] || 0) + (manager.meetingTypes['PIO'] || 0),
        paoMeetings: (manager.meetingTypes['PAO Interlock'] || 0) + (manager.meetingTypes['PAO'] || 0)
      }));
    return managerStats;
  }, [leaderboardMetrics]);

  const chartData = useMemo(() => {
    return [
      {
        name: 'Total Meetings',
        ...topManagers.reduce((acc, manager) => {
          acc[manager.name] = manager.totalMeetings;
          return acc;
        }, {})
      },
      {
        name: 'PIO Interlocks',
        ...topManagers.reduce((acc, manager) => {
          acc[manager.name] = manager.pioMeetings;
          return acc;
        }, {})
      },
      {
        name: 'PAO Interlocks',
        ...topManagers.reduce((acc, manager) => {
          acc[manager.name] = manager.paoMeetings;
          return acc;
        }, {})
      }
    ];
  }, [topManagers]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            Partner Manager Leaderboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Start Date"
              value={dateRange.start}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, start: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
            <DatePicker
              label="End Date"
              value={dateRange.end}
              onChange={(newValue) => setDateRange(prev => ({ ...prev, end: newValue }))}
              slotProps={{ textField: { size: 'small' } }}
            />
          </Box>
        </Stack>

        <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 3 }}>
          Showing data from {dateRange.start.format('MMM D, YYYY')} to {dateRange.end.format('MMM D, YYYY')}
        </Typography>

        {/* Top Stats Cards */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top Partner Manager
                </Typography>
                <Typography 
                  variant="h5" 
                  component="div" 
                  sx={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {topManagerStats?.topManager.name || 'N/A'}
                </Typography>
                <Typography color="text.secondary">
                  {topManagerStats?.topManager.meetings || 0} meetings
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Unique Partners
                </Typography>
                <Typography 
                  variant="h5" 
                  component="div"
                  sx={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {topManagerStats?.mostPartners.name || 'N/A'}
                </Typography>
                <Typography color="text.secondary">
                  {topManagerStats?.mostPartners.partners || 0} partners
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Most Interlocks
                </Typography>
                <Typography 
                  variant="h5" 
                  component="div"
                  sx={{ 
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {topManagerStats?.mostInterlocks.name || 'N/A'}
                </Typography>
                <Typography color="text.secondary">
                  {topManagerStats?.mostInterlocks.interlocks || 0} interlocks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Chart */}
        <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom align="center">
            Top 5 Partner Managers Performance
          </Typography>
          <Box sx={{ height: 400 }}>
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {topManagers.map((manager, index) => (
                  <Bar
                    key={manager.name}
                    dataKey={manager.name}
                    name={manager.name}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Paper>

        {/* Detailed Statistics */}
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Partner Manager</TableCell>
                <TableCell align="right">Total Meetings</TableCell>
                <TableCell align="right">Unique Partners</TableCell>
                <TableCell>Meeting Types</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboardMetrics.map((metric, index) => (
                <TableRow
                  key={index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCell component="th" scope="row">
                    {metric.name}
                  </TableCell>
                  <TableCell align="right">{metric.totalMeetings}</TableCell>
                  <TableCell align="right">{metric.uniquePartners}</TableCell>
                  <TableCell>
                    {Object.entries(metric.meetingTypes).map(([type, count], i) => (
                      <Chip
                        key={i}
                        label={`${type}: ${count}`}
                        size="small"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Weekly Statistics Tables */}
        <WeeklyStatsTable 
          title="Weekly Meeting Statistics" 
          type="total" 
        />
        <WeeklyStatsTable 
          title="Weekly PIO Interlock Statistics" 
          type="pio" 
        />
        <WeeklyStatsTable 
          title="Weekly PAO Interlock Statistics" 
          type="pao" 
        />
      </Box>
    </LocalizationProvider>
  );
}

export default Leaderboard;
